/**
 * Platform admins start a view-as session from this dialog. It checks the email shape before asking the backend,
 * because a malformed address only earns a plain 400, and it turns each view-as refusal into copy that tells the admin
 * what to do next. The stage mode is decided by the backend at start, so the note states the rule instead of guessing.
 */
import React, { useState } from 'react';
import { type Control, type FieldValues, useForm, useWatch } from 'react-hook-form';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { getErrorStatus, getViewAsRefusalCode } from 'src/lib/view-as/view-as-response';
import { useViewAs } from './useViewAs';

export interface ViewAsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface ViewAsFormValues {
  readonly email: string;
}

const MODAL_WIDTH_PX = 480;
const FORBIDDEN_STATUS = 403;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

const VIEW_AS_REFUSAL_MESSAGE_KEYS: ReadonlyMap<string, string> = new Map([
  ['VIEW_AS_DISABLED', 'view_as_disabled'],
  ['VIEW_AS_FORBIDDEN', 'view_as_forbidden'],
  ['VIEW_AS_INVALID_TARGET', 'view_as_invalid_email'],
  ['VIEW_AS_UNAVAILABLE', 'view_as_unavailable'],
]);

function normalizeEmail (email: string): string {
  return email.trim().toLowerCase();
}

function isEmailShape (email: string): boolean {
  return EMAIL_SHAPE.test(email);
}

function getStartFailureMessage (error: unknown, t: TranslateFn): string {
  const code = getViewAsRefusalCode(error);
  const key = code === undefined ? undefined : VIEW_AS_REFUSAL_MESSAGE_KEYS.get(code);
  if (key !== undefined) {
    return t(key);
  }
  const status = getErrorStatus(error);
  // The admin-only guard refuses a non-admin with a plain 403 that carries no view-as code.
  if (status === FORBIDDEN_STATUS) {
    return t('view_as_forbidden');
  }
  return status === undefined || status === 0
    ? t('view_as_start_failed')
    : t('view_as_start_failed_status', { status: String(status) });
}

/** Mounted only while the dialog is open, so every opening starts with an empty form. */
const ViewAsForm: React.FC<{ readonly onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const viewAs = useViewAs();
  const [failureMessage, setFailureMessage] = useState<string>();
  const { control, formState, getValues, handleSubmit } = useForm<ViewAsFormValues>({
    defaultValues: { email: '' },
    mode: 'onChange',
  });
  const email = useWatch({ control, name: 'email' });
  const isValid = isEmailShape(normalizeEmail(email));
  const isStarting = formState.isSubmitting;

  const onSubmit = async (values: ViewAsFormValues): Promise<void> => {
    setFailureMessage(undefined);
    try {
      await viewAs.start(normalizeEmail(values.email));
    } catch (error: unknown) {
      setFailureMessage(getStartFailureMessage(error, t));
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Column spacing="400">
        <Text>{t('view_as_dialog_description')}</Text>
        <FormInput
          autoComplete="off"
          control={control as unknown as Control<FieldValues>}
          data-testid="view-as-email"
          helperText={t('view_as_mode_note')}
          label={t('view_as_email_label')}
          name="email"
          type="email"
          validator={{
            fn: () => isEmailShape(normalizeEmail(getValues('email'))),
            message: t('view_as_invalid_email'),
          }}
        />
        {failureMessage !== undefined && (
          <Alert data-testid="view-as-start-error" type="error">{failureMessage}</Alert>
        )}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="300">
          <Button data-testid="view-as-cancel" disabled={isStarting} type="secondary" onClick={onClose}>
            {t('view_as_cancel')}
          </Button>
          <Button data-testid="view-as-start" disabled={!isValid || isStarting} submit type="primary">
            {isStarting ? <Loader size="small" /> : t('view_as_start')}
          </Button>
        </Row>
      </ModalFooter>
    </form>
  );
};

export const ViewAsDialog: React.FC<ViewAsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  if (!open) {
    return null;
  }
  return (
    <Modal data-testid="view-as-dialog" open title={t('view_as_user')} width={MODAL_WIDTH_PX} onClose={onClose}>
      <ViewAsForm onClose={onClose} />
    </Modal>
  );
};
